/**
 * CanalSubir — mini-web de subida para colaboradores del Canal.
 * Ruta pública /canal-subir. El empleado entra con su usuario/contraseña (creados
 * por la admin en "Colaboradores de subida") y SOLO puede subir vídeos a SU sección.
 * No usa la sesión del CRM: token propio (scope canal_uploader) en localStorage.
 */
import React, { useState, useEffect, useRef } from 'react';

const API = '/api/canal-uploaders';
const TKEY = 'canal_uploader_token';

export default function CanalSubir() {
  const [token, setToken] = useState(() => localStorage.getItem(TKEY) || '');
  const [info, setInfo] = useState(null); // { nombre, seccion_label }
  const [checking, setChecking] = useState(true);
  const [u, setU] = useState('');
  const [p, setP] = useState('');
  const [err, setErr] = useState('');
  const [logging, setLogging] = useState(false);
  const [subidas, setSubidas] = useState([]); // { name, estado, progreso }
  const fileRef = useRef(null);

  // Verifica el token al cargar.
  useEffect(() => {
    let alive = true;
    if (!token) { setChecking(false); return; }
    fetch(`${API}/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(d => { if (alive) { setInfo(d); setChecking(false); } })
      .catch(() => { if (alive) { localStorage.removeItem(TKEY); setToken(''); setChecking(false); } });
    return () => { alive = false; };
  }, [token]);

  const login = async (e) => {
    e?.preventDefault();
    setErr(''); setLogging(true);
    try {
      const res = await fetch(`${API}/login`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: u.trim().toLowerCase(), password: p }),
      });
      if (res.ok) {
        const d = await res.json();
        localStorage.setItem(TKEY, d.token);
        setInfo({ nombre: d.nombre, seccion_label: d.seccion_label });
        setToken(d.token); setP('');
      } else {
        const j = await res.json().catch(() => ({}));
        setErr(j.detail || 'Usuario o contraseña incorrectos');
      }
    } catch { setErr('No se pudo conectar.'); } finally { setLogging(false); }
  };

  const logout = () => { localStorage.removeItem(TKEY); setToken(''); setInfo(null); setSubidas([]); };

  const subirUno = (file, idx) => new Promise(async (resolve) => {
    const setEstado = (estado, progreso) => setSubidas(prev => prev.map((s, i) => i === idx ? { ...s, estado, progreso: progreso ?? s.progreso } : s));
    try {
      const r1 = await fetch(`${API}/upload-url`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ filename: file.name, content_type: file.type || '', size: file.size }),
      });
      if (!r1.ok) { const j = await r1.json().catch(() => ({})); setEstado('error: ' + (j.detail || 'no se pudo iniciar')); return resolve(); }
      const { presigned_url, id } = await r1.json();
      // PUT directo a S3 con progreso (XHR).
      await new Promise((res, rej) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presigned_url);
        if (file.type) xhr.setRequestHeader('Content-Type', file.type);
        xhr.upload.onprogress = (ev) => { if (ev.lengthComputable) setEstado('subiendo', Math.round(ev.loaded / ev.total * 100)); };
        xhr.onload = () => (xhr.status >= 200 && xhr.status < 300) ? res() : rej();
        xhr.onerror = () => rej();
        xhr.send(file);
      });
      await fetch(`${API}/confirm-upload`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ id }),
      });
      setEstado('listo', 100);
    } catch { setEstado('error: fallo de subida'); }
    resolve();
  });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    const base = subidas.length;
    setSubidas(prev => [...prev, ...files.map(f => ({ name: f.name, estado: 'en cola', progreso: 0 }))]);
    for (let i = 0; i < files.length; i++) await subirUno(files[i], base + i);
  };

  // ── Estilos mínimos (oscuro, móvil) ──
  const wrap = { minHeight: '100vh', background: '#0b0f0e', color: '#e8efec', display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '24px 16px', fontFamily: 'system-ui, sans-serif' };
  const card = { width: '100%', maxWidth: 440, background: '#141a18', border: '1px solid rgba(255,255,255,.08)', borderRadius: 16, padding: 20 };
  const input = { width: '100%', height: 44, borderRadius: 10, border: '1px solid rgba(255,255,255,.14)', background: '#0e1413', color: '#fff', padding: '0 12px', fontSize: 15, marginTop: 6, boxSizing: 'border-box' };
  const btn = { width: '100%', height: 46, borderRadius: 10, border: 'none', background: '#3f9c74', color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', marginTop: 14 };

  if (checking) return <div style={{ ...wrap, justifyContent: 'center' }}><div style={{ color: '#9aa6a1' }}>Cargando…</div></div>;

  // ── No logueado: login ──
  if (!token || !info) {
    return (
      <div style={wrap}>
        <div style={{ ...card, marginTop: '8vh' }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: '0 0 4px' }}>Subir vídeos · Canal</h1>
          <p style={{ color: '#9aa6a1', fontSize: 13, margin: '0 0 18px' }}>Entra con el usuario que te dio la clínica.</p>
          <form onSubmit={login}>
            <label style={{ fontSize: 12, color: '#9aa6a1' }}>Usuario</label>
            <input style={input} value={u} onChange={(e) => setU(e.target.value)} autoCapitalize="none" autoCorrect="off" placeholder="usuario" />
            <div style={{ height: 12 }} />
            <label style={{ fontSize: 12, color: '#9aa6a1' }}>Contraseña</label>
            <input style={input} type="password" value={p} onChange={(e) => setP(e.target.value)} placeholder="contraseña" />
            {err && <div style={{ color: '#f4a28a', fontSize: 13, marginTop: 10 }}>{err}</div>}
            <button style={{ ...btn, opacity: logging ? 0.6 : 1 }} disabled={logging}>{logging ? 'Entrando…' : 'Entrar'}</button>
          </form>
        </div>
      </div>
    );
  }

  // ── Logueado: subir ──
  return (
    <div style={wrap}>
      <div style={{ ...card }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 12, color: '#9aa6a1' }}>Hola{info.nombre ? `, ${info.nombre}` : ''}</div>
            <h1 style={{ fontSize: 18, fontWeight: 700, margin: '2px 0 0' }}>Subes a: <span style={{ color: '#3f9c74' }}>{info.seccion_label}</span></h1>
          </div>
          <button onClick={logout} style={{ background: 'none', border: 'none', color: '#9aa6a1', fontSize: 13, cursor: 'pointer' }}>Salir</button>
        </div>

        <button onClick={() => fileRef.current?.click()} style={{ ...btn, marginTop: 20 }}>📹 Subir vídeo (grabar o elegir)</button>
        <input ref={fileRef} type="file" accept="video/*" multiple style={{ display: 'none' }} onChange={onFiles} />
        <p style={{ color: '#9aa6a1', fontSize: 12, marginTop: 10 }}>
          Puedes grabar al momento o elegir del carrete. En iPhone, graba en formato "Compatible" (Ajustes → Cámara → Formatos) para que se vea en todos los dispositivos.
        </p>

        {subidas.length > 0 && (
          <div style={{ marginTop: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {subidas.map((s, i) => (
              <div key={i} style={{ background: '#0e1413', border: '1px solid rgba(255,255,255,.08)', borderRadius: 10, padding: '10px 12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                  <span style={{ fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{s.name}</span>
                  <span style={{ fontSize: 12, color: s.estado === 'listo' ? '#3f9c74' : String(s.estado).startsWith('error') ? '#f4a28a' : '#e8c98a', whiteSpace: 'nowrap' }}>
                    {s.estado === 'listo' ? '✓ Subido' : s.estado === 'subiendo' ? `${s.progreso}%` : s.estado}
                  </span>
                </div>
                {s.estado === 'subiendo' && (
                  <div style={{ height: 4, background: 'rgba(255,255,255,.1)', borderRadius: 4, marginTop: 8, overflow: 'hidden' }}>
                    <div style={{ height: '100%', width: `${s.progreso}%`, background: '#3f9c74', transition: 'width .2s' }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
